/**
 * Models for the Action service
 * @packageDocumentation
 * @module ActionService
 * 
 * interface AppElementTypes { "ElementType" : ElementTypeDTO }
 * 
 */

import { ErrorWithCode, RESERVED_ERROR_CODES } from "@cervval.js/common/src/errors/errors";
import { anyReallyAny, stringifyIfNeeded } from "@cervval.js/common/src/tools/object.tools";
import { UnixTimestamp } from "@cervval.js/common/src/tools/timestamps.tools";
import { ActionCacheService } from "./services/cache.service";
import { ID } from "./types";

export enum OperationType {
    CREATE = "CREATE",
    UPDATE = "UPDATE",
    DELETE = "DELETE",
    ADD = "ADD",
    REMOVE = "REMOVE",
}

export const DEFAULT_OP_UPDATE_VALUE_CONVERTER: OpUpdateValueConverter<unknown, unknown, anyReallyAny, anyReallyAny, anyReallyAny, anyReallyAny> =
    (action, elementId, field, oldValue, newValue) => {
        return {
            oldValue: stringifyIfNeeded(oldValue),
            newValue: stringifyIfNeeded(newValue)
        };
    };

export type OpUpdateValueConverter<
    AppLogInfos,
    Context,
    ElementTypes,
    TableName extends keyof ElementTypes = keyof ElementTypes,
    ElementType extends ElementTypes[TableName] = ElementTypes[TableName],
    Field extends keyof ElementType = keyof ElementTypes[TableName]> = (action: Action<ElementTypes, AppLogInfos, Context>, elementId: number, field: Field, oldValue: ElementType[Field], newValue: ElementType[Field]) => { oldValue: string, newValue: string };

export interface Operation<ElementTypes, AppLogInfos, Context> {
    /**
     * process operation changes (this function is called when processing the operation for the first time or when redoing an undone operation)
     */
    process: (action: Action<ElementTypes, AppLogInfos, Context>) => void;

    /**
     * undo operation changes
     */
    undo: (action: Action<ElementTypes, AppLogInfos, Context>) => void;

    /**
     * update internal objects (retrieve the element from the cache or update ids and foreign keys)
     */
    actualize(action: Action<ElementTypes, AppLogInfos, Context>): void;

    /**
     * Add server server action element to ActionPersistenceData
     */
    addServerActionElement(actionPersistData: ActionPersistData<Context>, params: { elementsToUpdateByTypeAndId: Record<string, ElementToUpdate>, action: Action<ElementTypes, AppLogInfos, Context>, actionType: ACTION_TYPE }): void;

    /**
     * Returns the type of the current Operation (Create/Delete/Update/...)
     * @returns {OperationType}
     */
    getOperationType(): OperationType;

    /** 
     * @returns the manipulated element object
     */
    getElement(): unknown;

    /**
     * @returns stringified operation for console display (not user friendly)
     */
    toString: (action: Action<ElementTypes, AppLogInfos, Context>) => string;

    /** type of element this operation is applied to */
    readonly elementTypeName: keyof ElementTypes;
}

export type ActionPersistRequestParams<LoginData> = {
    loginData?: LoginData,
    applicationName?: string
};

export type ActionPersistData<ActionContext> = {
    userSocketUid?: string,
    notifyRooms: string[],// used for websocket messages
    logMessage: string,
    clientTime: UnixTimestamp,
    logInfos?: unknown,
    elementsToCreate?: ElementToCreate[],
    elementsToDelete?: ElementToDelete[],
    elementsToUpdate?: ElementToUpdate[],
    /** context is mandatory for server-side actions */
    context?: ActionContext
};

/** Summary of all impacted objects for one elementTypeName */
export interface ActionPersistSummary {
    /** if changes are numerous or hard to track, we mark the whole table dirty  */
    global?: boolean;
    deletedIds?: ID[];
    updatedIds?: ID[];
    createdIds?: ID[];
}

/** ActionPersistSummary with timestamp */
export interface DatedActionPersistSummary extends ActionPersistSummary {
    time: UnixTimestamp;
}

export interface ActionPersistResult {
    time: UnixTimestamp;
    updateSummary: { [elementTypeName: string]: ActionPersistSummary };
    updatedIds: { [elementTypeName: string]: { [oldId: number]: number } };
    updatedForeignKeys?: {
        elementTypeName: string,
        elementId: number,  // NB : can be negative
        field: string,
        newId: number,
        oldId: number
    }[];
    error?: ErrorWithCode;
}

export type ElementToUpdate = {
    elementTypeName: string,
    elementId: number,
    // TODO : Modify json.parse/stringify to be able to use a map
    fieldsToUpdate: { [field: string]: unknown },

    valuesToAdd?: { [field: string]: unknown[] }, // value can be a referenceId, a value or a DTO depending on field
    valuesToRemove?: { [field: string]: unknown[] },// value can be a referenceId, a value or a DTO depending on field

    /** 
     * Old values display string for operation logs.
     */
    oldValuesDisplayString: { [field: string]: string },
    /** 
     * New values display string for operation logs.
     */
    newValuesDisplayString: { [field: string]: string }
};

export type ElementToDelete = {
    elementTypeName: string,
    elementId: number
};

export type ElementToCreate<T = unknown> = {
    elementTypeName: string,
    element: T,
    isRecreate?: boolean
};

export type ElementOperation = ElementToUpdate | ElementToCreate | ElementToDelete;


// error codes from 900 to 919 are reserved for action errors
export enum ErrorCodes {
    RESERVED = RESERVED_ERROR_CODES, // * You can check all reserved codes here

    UNEXPECTED_ERROR = 900,
    OFFLINE_CANT_WAIT_FOR_REPLY = 901
}


export enum ACTION_TYPE {
    UNDO = "UNDO",
    REDO = "REDO",
    PROCESS = "PROCESS"
}

export enum ACTION_STATUS {
    TO_BE_SENT = "TO_BE_SENT",
    SEND_IN_PROGRESS = "SEND_IN_PROGRESS",
    SENT = "SENT",
    NO_SEND_NEEDED = "NO_SEND_NEEDED",
    ERROR = "ERROR"
}

export type ActionInfo<ElementTypes, ActionContext> = {
    userSocketUid?: string,
    action: Action<ElementTypes, unknown, ActionContext>,
    type: ACTION_TYPE,
    status: ACTION_STATUS,
    // FIXME clientTime: UnixTimestamp,
    offline?: boolean,

    // waitForReplyPromise functions are called once persist is handled and local cache is updated
    waitForReplyPromise?: { resolve: (value: unknown) => void, reject: (error: unknown) => void },

    // Context of the action
    context?: ActionContext
};

export interface ActionParams<ElementTypes, AppLogInfos, ActionContext> {

    /**
     * Set to true if you don't want this action to be registered in the undo system on completion
     * ! you should almost never set this to true since it may lead to inconsistency between user actions and undo stack
     * ! if you need to set doNotRegisterForUndo to true please ask for advice before and add a comment to explain why you did it
     */
    doNotRegisterForUndo?: boolean;

    /**
     * Rooms to notify (for push notification, usually web sockets)
     */
    notifyRooms?: string[];

    /**
     * Action summary message that will be logged and that can be displayed to users (only available in default language)
     */
    logMessage: string;

    /**
     * Additional information to store in the log (to ease searching through the log)
     */
    logInfos?: AppLogInfos;

    /**
     * If true, the encapsulateInAction Promise will be resolved only once the persistence is done and element ids are updated.
     * You usually do NOT need to set this to true. This makes the user experience less 'smooth'. Before setting this to true, ask for advice to "Action experts".
     * Also, if your application is expected to work offline, you can't use waitForReply (encapsulateInAction will crash)
     */
    waitForReply?: boolean;

    // TODO stopOnError?

    /**
     * Promise called after all process Operation (but local cache not updated yet) and before they are persisted 
     */
    onAfterProcessOperation?: (action: Action<ElementTypes, AppLogInfos, ActionContext>, result: unknown) => Promise<void>;

    /**
     * This function is called before sending data to the server for persistence
     */
    onBeforePersist?: (action: ActionInfo<ElementTypes, ActionContext>) => Promise<void>;

    /**
     * This function is called after the server has replied (even if there was an error)
     */
    onAfterPersist?: (action: Action<ElementTypes, AppLogInfos, ActionContext>, persistedActions: Action<ElementTypes, AppLogInfos, ActionContext>[], result: ActionPersistResult) => Promise<void>;

    /**
     * Callback called after all actions have been process in server side and local cache have been updated in client side
     */
    onAfterUpdateLocalCache?: (actions: Action<ElementTypes, AppLogInfos, ActionContext>[], result: ActionPersistResult) => Promise<void>;

    /**
     * This function is called after onAfterPersist if an error occurred
     * This method will be ignored if user set waitForReply at true.
     * @returns false if error was handled, true is error still needs to be handled (by default handler)
     */
    onPersistError?: (action: ActionInfo<ElementTypes, ActionContext>, sentActions: ActionInfo<ElementTypes, ActionContext>[], result: ActionPersistResult | ErrorWithCode) => Promise<boolean>;

    /** 
     * Context of action 
     * If set, we will use this context as Context for the action, we won't ask to ActionContextService the current context
     * context is mandatory for server-side actions, with ActionPersistRequestParams, cf. {@link ServerActionContext<LoginData>}
     */
    context?: ActionContext;

    /**
     * Cache used by the action ({@link Action._cacheManager}), mandatory when creating server-side actions
     */
    actionCacheService?: ActionCacheService<ElementTypes>;

    /**
     * User-side defined converter to build custom string message from elementId (delete), partialDTO (key-value / update or create)
     */
    opUpdateValueConverter?: OpUpdateValueConverter<AppLogInfos, ActionContext, ElementTypes>;

}

export interface Action<ElementTypes = unknown, AppLogInfos = unknown, ActionContext = unknown> {

    /**
     * Ordered list of processed operations
     */
    operations: Operation<ElementTypes, AppLogInfos, ActionContext>[];

    /**
     * 1 sets a temporary (negative) id on objectToCreate
     * 2 registers the object in the local cache
     * 3 adds the create operation to the operations list to be persisted on action termination
     * @returns objectToCreate
     */
    create<TypeName extends keyof ElementTypes>(elementTypeName: TypeName, objectToCreate: ElementTypes[TypeName]): ElementTypes[TypeName];

    /**
     * 1 deletes the object from the local cache
     * 2 adds the delete operation to the operations list to be persisted on action termination
     */
    delete<TypeName extends keyof ElementTypes>(elementTypeName: TypeName, objectToDelete: ElementTypes[TypeName]): void;

    /**
     * If newValue is different from the current one
     * 1 does `objectToUpdate[field] = newValue`
     * 2 adds the update operation to the operations list to be persisted on action termination
     */
    updateValueIfNeeded<
        TypeName extends keyof ElementTypes,
        T extends ElementTypes[TypeName],
        K extends keyof T,
        V extends T[K] = T[K]
    >(elementTypeName: TypeName, objectToUpdate: T, field: K, newValue: V): boolean;

    /**
     * Multiple fields update, call updateValueIfNeeded for all fields of newValues ({@link updateValueIfNeeded})
     * @returns true if an update (at least) has been proceed
     */
    updateValuesIfNeeded<
        TypeName extends keyof ElementTypes,
        T extends ElementTypes[TypeName]
    >(elementTypeName: TypeName, objectToUpdate: T, newValues: Partial<T>): boolean;

    /**
     * Add value to an array if not already present (values are usually stored in an associative table when storage is a relational database)
     * @param elementTypeName usually the table name
     * @param objectToUpdate the object to update
     * @param field the name of the property that holds the array
     * @param newValueToAdd the value to add (for now only reference ids are implemented)
     * @param ignoredDuplicate adds the value even if it is already present in objectToUpdate[field]
     * MAB: Using the ignoredDuplicate option should be done only if you know what you do
     * @returns true if added
     */
    addToArray<
        TypeName extends keyof ElementTypes,
        T extends ElementTypes[TypeName]
    >(elementTypeName: TypeName, objectToUpdate: T, field: keyof ElementTypes[TypeName],
        newValueToAdd: { addReference: { id: number } },// TODO replace with AddToArrayValue once implemented
        ignoredDuplicate?: boolean // 
    ): boolean;

    /**
     * Remove value from array (values are usually stored in an associative table when storage is a relational database)
     * @param elementTypeName usually the table name
     * @param objectToUpdate the object to update
     * @param field the name of the property that holds the array
     * @param valueToRemove the value to remove (for now only reference ids are implemented)
     * @returns true if removed
     */
    removeFromArray<
        TypeName extends keyof ElementTypes,
        T extends ElementTypes[TypeName]
    >(elementTypeName: TypeName, objectToUpdate: T, field: keyof ElementTypes[TypeName],
        valueToRemove: { addReference: { id: number } }// TODO replace with AddToArrayValue once implemented
    ): boolean;

    /**
     * @returns {ActionCacheService}
     */
    getCacheManager(): ActionCacheService<ElementTypes>;

    /**
     * ! You should not need to call this, ask an expert before doing so !
     * Set the cache manager, use by detached action mechanism to put back and sync the global cache
     */
    setCacheManager(cacheService: ActionCacheService<ElementTypes>): void;

    /** Parameters passed to encapsulateInAction */
    params: ActionParams<ElementTypes, AppLogInfos, ActionContext>;

    /**
     * On client side, context may be navigation information
     * On server side, context may be the current Request
     */
    context?: ActionContext;

    /**
     * Undo the effect of action (undo of every operations)
     */
    undo(): void;

    /**
     * Redo the effect of action (redo of every operations)
     */
    redo(): void;

    /**
     * Timestamp of the action client-side. !Do not trust this
     */
    clientTime: UnixTimestamp;

}

